// ============================
// 实验室预约系统 - 常量与类型定义
// ============================

// ---------- 环境展示状态 ----------
export const enum EnvDisplayStatus {
    Shell = 'shell',
    Future = 'future',
    Preparing = 'preparing',
    Running = 'running',
    Stopping = 'stopping',
    Stopped = 'stopped',
    NotRunning = 'not_running',
    Releasing = 'releasing',
    Released = 'released',
    Failed = 'failed',
    Unknown = 'unknown',
}

// ---------- 预约状态（对齐后端 domain/enums.py::ReservationStatus） ----------
export const enum ReservationStatus {
    Pending = 'pending',
    Preparing = 'preparing',
    Migrating = 'migrating',
    Ready = 'ready',
    Starting = 'starting',
    Running = 'running',
    Ending = 'ending',
    EarlyReleasing = 'early_releasing',
    Completed = 'completed',
    Cancelled = 'cancelled',
    Overridden = 'overridden',
    StartFailed = 'start_failed',
    ReleaseFailed = 'release_failed',
    Unknown = 'unknown',
}

/** 未开始、仍可取消的状态集合（后端 RESERVATION_ACTIVE_FUTURE_STATUSES） */
export const RESERVATION_ACTIVE_FUTURE_STATUSES: string[] = [
    ReservationStatus.Pending,
    ReservationStatus.Preparing,
    ReservationStatus.Migrating,
    ReservationStatus.Ready,
];

// ---------- 预约来源（后端只有这 5 个） ----------
export const enum ReservationSource {
    Self = 'self',
    Team = 'team',
    Group = 'group',
    AdminResource = 'admin_resource',
    AdminGroup = 'admin_group',
}

/** 用户可自行取消的来源：自主、队伍和管理员代用户创建的资源预约 */
export const USER_CANCELLABLE_SOURCES: string[] = [ReservationSource.Self, ReservationSource.Team, ReservationSource.AdminResource];

// ---------- 时段状态 ----------
export const enum SlotStatus {
    Available = 'available',
    Past = 'past',
    NotOpen = 'not_open',
    TicketExpired = 'ticket_expired',
    TicketInsufficient = 'ticket_insufficient',
    CapacityFull = 'capacity_full',
    Conflict = 'conflict',
    /** 后端用于实时预约/15 分钟截止等业务门禁的不可选状态。 */
    Unavailable = 'unavailable',
    Unknown = 'unknown',
}

// ---------- 券账户状态（后端 TicketAccountStatus） ----------
// ⚠ 后端取值是 normal / expired / released，**没有 active**。
// 实盘验证：GET /lab/me/ticket-accounts 返回 "ticket_status":"normal"。
// 原来写成 Active='active'，导致按此过滤的地方恒为空。
export const enum TicketAccountStatus {
    Normal = 'normal',
    Expired = 'expired',
    Released = 'released',
    Unknown = 'unknown',
}

// ---------- 时段配置 ----------
export const SLOT_DURATION_HOURS = 6;
export const SLOT_DURATION_MINUTES = 360;
export const SLOTS_PER_DAY = 4;
export const DEFAULT_DAYS_PER_PAGE = 7;
export const MAX_DAYS_PERSONAL = 7;
export const MAX_DAYS_GROUP = 30;
export const RESERVATION_CUTOFF_MINUTES = 15;

export const isReservationCutoffReached = (slotStartAt: string, now = Date.now()): boolean => {
    return new Date(slotStartAt).getTime() - now <= RESERVATION_CUTOFF_MINUTES * 60 * 1000;
};

export const canManagePersonalReservation = (
    account: {
        can_manage_reservations: boolean;
        ticket_status?: string | null;
        environment_status?: string | null;
        release_at?: string | null;
        activity_status?: string | null;
        activity_end_at?: string | null;
    },
    now = Date.now()
): boolean => {
    if (!account.can_manage_reservations || account.ticket_status !== TicketAccountStatus.Normal || account.environment_status !== 'active')
        return false;
    if (account.release_at && now >= new Date(account.release_at).getTime()) return false;
    if (account.activity_status && account.activity_status !== 'active') return false;
    if (account.activity_end_at && now >= new Date(account.activity_end_at).getTime()) return false;
    return true;
};

// ---------- 轮询配置 ----------
export const POLLING_INITIAL_INTERVAL = 3000;
export const POLLING_BACKOFF_INTERVAL = 10000;
export const POLLING_BACKOFF_AFTER = 30000;
export const POLLING_BACKGROUND_INTERVAL = 30000;

// ---------- 国际化状态映射 ----------
export const getReservationStatusMap = (t: (key: string) => string) =>
    new Map<ReservationStatus, { type: string; text: string }>([
        [ReservationStatus.Pending, { type: 'info', text: t('lab.reservation.statusPending') }],
        [ReservationStatus.Preparing, { type: 'info', text: t('lab.reservation.statusPending') }],
        [ReservationStatus.Migrating, { type: 'info', text: t('lab.reservation.statusPending') }],
        [ReservationStatus.Ready, { type: 'info', text: t('lab.reservation.statusPending') }],
        [ReservationStatus.Starting, { type: 'success', text: t('lab.reservation.statusRunning') }],
        [ReservationStatus.Running, { type: 'success', text: t('lab.reservation.statusRunning') }],
        [ReservationStatus.Ending, { type: 'success', text: t('lab.reservation.statusRunning') }],
        [ReservationStatus.EarlyReleasing, { type: 'success', text: t('lab.reservation.statusRunning') }],
        [ReservationStatus.Completed, { type: 'info', text: t('lab.reservation.statusCompleted') }],
        [ReservationStatus.Cancelled, { type: 'info', text: t('lab.reservation.statusCancelled') }],
        [ReservationStatus.Overridden, { type: 'info', text: t('lab.reservation.statusCancelled') }],
        [ReservationStatus.StartFailed, { type: 'danger', text: t('lab.reservation.statusFailed') }],
        [ReservationStatus.ReleaseFailed, { type: 'danger', text: t('lab.reservation.statusFailed') }],
        [ReservationStatus.Unknown, { type: 'danger', text: t('lab.reservation.statusUnknown') }],
    ]);

/**
 * 前端预判「能否取消」，规则与后端 policies.can_user_cancel 保持一致：
 * 来源必须是自主/队伍/管理员代用户创建的资源预约、状态必须未开始、且当前时间早于开始时间。
 * 三者缺一不可——只判 pending 会把 preparing/ready 的可取消预约误锁死。
 */
export const canUserCancelReservation = (
    reservation: { source?: string | null; status?: string | null; start_at?: string | null },
    now = Date.now()
): boolean => {
    if (!reservation.source || !USER_CANCELLABLE_SOURCES.includes(reservation.source)) return false;
    if (!reservation.status || !RESERVATION_ACTIVE_FUTURE_STATUSES.includes(reservation.status)) return false;
    if (!reservation.start_at) return false;
    return now < new Date(reservation.start_at).getTime();
};

export const getReservationTicketUnitKey = (reservation: {
    ticket_type?: string | null;
    source?: string | null;
    owner_type?: string | null;
}): string => {
    if (reservation.ticket_type === 'group') return 'lab.reservation.groupTicketUnit';
    if (reservation.source === ReservationSource.Team || reservation.owner_type === 'team') {
        return 'lab.reservation.teamTicketUnit';
    }
    return 'lab.reservation.ticketUnit';
};

export const getSlotStatusMap = (t: (key: string) => string) =>
    new Map<SlotStatus, { text: string; style: string }>([
        [SlotStatus.Available, { text: '', style: 'slot-available' }],
        [SlotStatus.Past, { text: t('lab.reservation.slotPast'), style: 'slot-past' }],
        [SlotStatus.NotOpen, { text: t('lab.reservation.slotNotOpen'), style: 'slot-not-open' }],
        [SlotStatus.TicketExpired, { text: t('lab.reservation.slotTicketExpired'), style: 'slot-expired' }],
        [SlotStatus.TicketInsufficient, { text: t('lab.reservation.slotTicketInsufficient'), style: 'slot-insufficient' }],
        [SlotStatus.CapacityFull, { text: t('lab.reservation.slotFull'), style: 'slot-full' }],
        [SlotStatus.Conflict, { text: t('lab.reservation.slotConflict'), style: 'slot-conflict' }],
        [SlotStatus.Unavailable, { text: t('lab.reservation.slotUnavailable'), style: 'slot-unavailable' }],
        [SlotStatus.Unknown, { text: t('lab.reservation.slotUnknown'), style: 'slot-unknown' }],
    ]);

export const getReservationSourceMap = (t: (key: string) => string) =>
    new Map<ReservationSource, string>([
        [ReservationSource.Self, t('lab.reservation.sourceSelf')],
        [ReservationSource.Team, t('lab.reservation.sourceTeam')],
        [ReservationSource.Group, t('lab.reservation.sourceGroup')],
        [ReservationSource.AdminResource, t('lab.reservation.sourceAdminResource')],
        [ReservationSource.AdminGroup, t('lab.reservation.sourceAdminGroup')],
    ]);

export const getEnvDisplayStatusMap = (t: (key: string) => string) =>
    new Map<EnvDisplayStatus, { type: string; text: string }>([
        [EnvDisplayStatus.Shell, { type: 'info', text: t('lab.reservation.envShell') }],
        [EnvDisplayStatus.Future, { type: 'info', text: t('lab.reservation.envShell') }],
        [EnvDisplayStatus.Preparing, { type: 'info', text: t('lab.reservation.envShell') }],
        [EnvDisplayStatus.Running, { type: 'success', text: t('lab.statusRunning') }],
        [EnvDisplayStatus.Stopping, { type: 'success', text: t('lab.statusRunning') }],
        [EnvDisplayStatus.Stopped, { type: 'info', text: t('lab.statusStopped') }],
        [EnvDisplayStatus.NotRunning, { type: 'info', text: t('lab.reservation.envShell') }],
        [EnvDisplayStatus.Releasing, { type: 'danger', text: t('lab.statusFailed') }],
        [EnvDisplayStatus.Released, { type: 'danger', text: t('lab.statusFailed') }],
        [EnvDisplayStatus.Failed, { type: 'danger', text: t('lab.statusFailed') }],
        [EnvDisplayStatus.Unknown, { type: 'danger', text: t('lab.statusFailed') }],
    ]);
